import { Tabs } from "@/components/ui/tabs";
import { useSearchParams, useNavigate } from "react-router-dom";
import { TabsList } from "./tabs/TabsList";
import { TabsContentSection } from "./tabs/TabsContent";
import { useProfile } from "@/hooks/useProfile";
import Shimmer from "../ui/shimmer";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ConfigTabsProps {
  isAdmin: boolean;
  userRole?: string;
}

export const ConfigTabs = ({ isAdmin, userRole }: ConfigTabsProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile, isLoading } = useProfile();
  const activeTab = searchParams.get("tab") || "perfil";

  const { data: gym } = useQuery({
    queryKey: ["gym-info", profile?.gym_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("gyms")
        .select("id, name")
        .eq("id", profile!.gym_id!)
        .single();
      return data;
    },
    enabled: !!profile?.gym_id,
  });

  const isLaBarraProfesor =
    userRole === "profesor" && gym?.name?.toLowerCase().includes("la barra");
  
  const showClassesTab = !isAdmin && userRole === "profesor";
  const showAdminTab = isAdmin || userRole === "manager";
  const showScheduleTab = !!isLaBarraProfesor;

  let tabCount = 2; // perfil + estilos
  if (showClassesTab) tabCount++;
  if (showAdminTab) tabCount++;
  if (showScheduleTab) tabCount++;

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", value);
    if (value === "admin") {
      newParams.set("subtab", "estadisticas");
    } else {
      newParams.delete("subtab");
    }
    navigate(`/configuracion?${newParams.toString()}`);
  };

  if (isLoading) {
    return (
      <div className="w-full animate-fade-in space-y-6">
        <div className={`grid w-full grid-cols-${tabCount} gap-4`}>
          {Array.from({ length: tabCount }).map((_, i) => (
            <Shimmer key={i} className="h-10 rounded-lg" />
          ))}
        </div>
        <Shimmer className="h-[400px] rounded-xl" />
      </div>
    );
  }

  return (
    <Tabs 
      defaultValue={activeTab} 
      value={activeTab} 
      className="w-full animate-fade-in settings-header"
      onValueChange={handleTabChange}
    >
      <TabsList
        showClassesTab={showClassesTab}
        showAdminTab={showAdminTab}
        showScheduleTab={showScheduleTab}
        tabCount={tabCount}
        userRole={userRole}
      />
      <TabsContentSection
        showClassesTab={showClassesTab}
        showAdminTab={showAdminTab}
        showScheduleTab={showScheduleTab}
        userRole={userRole}
        defaultSubtab={searchParams.get("subtab") || undefined}
      />
    </Tabs>
  );
};
